import { Box, Flex, Tooltip } from '@mantine/core';
import { memo } from 'react';
import {
  EntityCategory,
  getLabelId,
  selectChange,
  selectExistingFields,
  selectIsDiscarded,
  selectIsResolved,
  selectLastAction,
  selectNewFields,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import { FieldTypeMetadata, FieldValue } from '../types';
import { DataInput } from './DataInput';
import EntityCell from './EntityCell';
import ValueChange from './ValueChange';
import { camelCaseOrSnakeCaseToWords } from '../merge-review/ReviewHelperFunctions';

type Props = {
  detectionId: string;
  category: EntityCategory;
};

function SimpleEntityData({ detectionId, category }: Props) {
  const newFields = useDetectedEntitiesStore(state =>
    selectNewFields(state, category, detectionId),
  );

  return (
    <Flex direction="column" gap="xxs">
      {newFields.map(({ key, value, metadata, isReadOnly }) => {
        return (
          <Tooltip
            key={key}
            label="Updating first and last name with this feature is not allowed."
            disabled={!isReadOnly || Array.isArray(value)}
            withArrow
            arrowSize={7}
          >
            <Box>
              <EntityCell
                title={metadata.label || camelCaseOrSnakeCaseToWords(key)}
                NewComponent={
                  <NewField
                    detectionId={detectionId}
                    fieldName={key}
                    value={value}
                    metadata={metadata}
                    isReadOnly={isReadOnly}
                    category={category}
                  />
                }
                ExistingComponent={
                  <ExistingField category={category} detectionId={detectionId} fieldName={key} />
                }
                category={category}
                detectionId={detectionId}
                fieldName={key}
                isDisabled={isReadOnly}
              />
            </Box>
          </Tooltip>
        );
      })}
    </Flex>
  );
}

function NewField({
  detectionId,
  fieldName,
  value,
  metadata,
  isReadOnly,
  category,
}: {
  detectionId: string;
  fieldName: string;
  value: FieldValue;
  metadata: FieldTypeMetadata;
  isReadOnly: boolean;
  category: EntityCategory;
}) {
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detectionId, fieldName),
  );
  const lastAction = useDetectedEntitiesStore(state =>
    selectLastAction(state, category, detectionId),
  );
  const didInsert = lastAction?.type === 'insert' || (category === 'new' && isResolved);
  const labelId = getLabelId(category, detectionId, fieldName);

  if (isResolved && !isReadOnly && !didInsert) {
    return null;
  }

  return (
    <DataInput
      editable={!isReadOnly}
      highlight="new"
      metadata={metadata}
      category={category}
      detectionId={detectionId}
      fieldName={fieldName}
      ariaLabelledBy={labelId}
    >
      {value}
    </DataInput>
  );
}

function ExistingField({
  detectionId,
  fieldName,
  category,
}: {
  detectionId: string;
  fieldName: string;
  category: EntityCategory;
}) {
  const isDiscarded = useDetectedEntitiesStore(state =>
    selectIsDiscarded(state, category, detectionId, fieldName),
  );
  const isResolved = useDetectedEntitiesStore(state =>
    selectIsResolved(state, category, detectionId, fieldName),
  );
  const change = useDetectedEntitiesStore(state =>
    selectChange(state, category, detectionId, fieldName),
  );
  const existingFields = useDetectedEntitiesStore(state =>
    selectExistingFields(state, category, detectionId),
  );
  const field = existingFields.find(field => field.key === fieldName);

  if (field == null) {
    return null;
  }

  const { value, isReadOnly } = field;

  if (!isReadOnly && isDiscarded) {
    return null;
  }

  if (!isReadOnly && isResolved && change != null) {
    return (
      <ValueChange
        newValue={change.value}
        category={category}
        previousValue={change.previousValue}
        metadata={field.metadata}
      />
    );
  }

  return (
    <DataInput editable={false} highlight="existing" metadata={field.metadata} category={category}>
      {value}
    </DataInput>
  );
}

export default memo(SimpleEntityData);
